import React from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import type { FlowComponentRenderProps, NpsFormComponentConfig } from '../../types';
import HtmlText from '../HtmlText';
import InlineFeedback from './InlineFeedback';
import RatingSubmitArea from './RatingSubmitArea';
import {
  buildRatingValue,
  defaultButtonStyles,
  getRatingState,
  selectedButtonStyles,
} from './ratingUtils';

const NpsFormBlock: React.FC<FlowComponentRenderProps<NpsFormComponentConfig>> = ({
  config,
  goNext,
  setValue,
  submitValueAndGoNext,
  value,
}) => {
  const ratingState = getRatingState(value);
  const selectedValue = ratingState.score;
  const submitted = ratingState.submitted;
  const selectionLocked = submitted && config.allowResubmit === false;
  const lowLabel = config.lowLabel ?? 'Not likely';
  const highLabel = config.highLabel ?? 'Very likely';
  const legacyStyle = ['pills', 'circles', 'tiles', 'segments'].includes(config.layout ?? '')
    ? (config.layout as 'pills' | 'circles' | 'tiles' | 'segments')
    : null;
  const layout = legacyStyle ? 'split' : config.layout ?? 'split';
  const buttonStyle = config.style ?? legacyStyle ?? 'standard';
  const colorCoding = config.colorCoding === true;
  const detractorMax = Math.min(8, Math.max(0, config.detractorMax ?? 6));
  const neutralMax = Math.min(9, Math.max(detractorMax + 1, config.neutralMax ?? 8));
  const scores = Array.from({ length: 11 }, (_, score) => score);
  const scoreRows =
    layout === 'single_row'
      ? [scores]
      : layout === 'grid_4_4_3'
        ? [scores.slice(0, 4), scores.slice(4, 8), scores.slice(8)]
        : layout === 'grid_3_4_4'
          ? [scores.slice(0, 3), scores.slice(3, 7), scores.slice(7)]
          : layout === 'grouped_bands'
            ? [
                scores.filter((score) => score <= detractorMax),
                scores.filter((score) => score > detractorMax && score <= neutralMax),
                scores.filter((score) => score > neutralMax),
              ]
            : layout === 'vertical'
              ? scores.map((score) => [score])
              : [scores.slice(0, 5), scores.slice(5)];
  const scoreColor = (score: number) =>
    score <= detractorMax
      ? config.detractorColor ?? '#FECACA'
      : score <= neutralMax
        ? config.neutralColor ?? '#FEF3C7'
        : config.promoterColor ?? '#DCFCE7';
  const renderScoreButton = (score: number) => {
    const selected = selectedValue === score;

    return (
      <Button
        key={score}
        aria-pressed={selected}
        disabled={selectionLocked}
        onClick={() => setValue(config.id, buildRatingValue(config, ratingState, score, false))}
        sx={{
          ...(selected ? selectedButtonStyles : defaultButtonStyles),
          bgcolor: selected
            ? 'var(--cx-brand-button-selected-bg)'
            : colorCoding
              ? scoreColor(score)
              : 'var(--cx-brand-button-bg)',
          border: '1px solid',
          borderRadius:
            buttonStyle === 'circles'
              ? '50%'
              : buttonStyle === 'pills'
                ? 999
              : buttonStyle === 'segments'
                  ? 0
                  : 0.75,
          fontSize: 17,
          fontWeight: 600,
          height: layout === 'vertical' ? 40 : 48,
          minHeight: 48,
          minWidth: 0,
          px: 0,
          transition: selected ? 'none' : undefined,
          '&:hover': {
            bgcolor: selected
              ? 'var(--cx-brand-button-selected-bg)'
              : colorCoding
                ? scoreColor(score)
                : 'var(--cx-brand-button-hover-bg)',
            borderColor: selected
              ? 'var(--cx-brand-button-selected-border)'
              : 'var(--cx-brand-button-border)',
          },
          '&:disabled': {
            color: selected
              ? 'var(--cx-brand-button-selected-text)'
              : 'var(--cx-brand-text-muted)',
            opacity: 0.75,
          },
        }}
        type="button"
        variant="outlined"
      >
        {score}
      </Button>
    );
  };

  return (
    <Card
      sx={{
        bgcolor: 'transparent',
        border: 0,
        borderRadius: 0,
        boxShadow: 'none',
        color: 'var(--cx-brand-text)',
        p: 0,
      }}
    >
      <Stack spacing={1.5}>
        <HtmlText
          component="div"
          html={config.question ?? 'How likely are you to recommend us?'}
          variant="subtitle2"
          sx={{ fontSize: 16, fontWeight: 600, lineHeight: '24px', textAlign: 'center' }}
        />
        <Stack spacing={0.75}>
          <Stack spacing={buttonStyle === 'segments' ? 0 : 0.75}>
            {scoreRows.map((row, rowIndex) => (
              <Box
                key={`nps-row-${rowIndex}`}
                sx={{
                  alignSelf: layout === 'split' && row.length === 5 ? 'center' : undefined,
                  display: 'grid',
                  gap: buttonStyle === 'segments' ? 0 : buttonStyle === 'tiles' ? 0.4 : 0.75,
                  gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
                  maxWidth:
                    layout === 'vertical'
                      ? 180
                      : layout === 'split' && row.length === 5
                        ? 'calc((((100% - 40px) / 6) * 5) + 32px)'
                        : undefined,
                  width: '100%',
                }}
              >
                {row.map(renderScoreButton)}
              </Box>
            ))}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: 'var(--cx-brand-text)', fontSize: 14, lineHeight: 1.35 }}
            >
              0 = <Box component="span">{lowLabel}</Box>
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'var(--cx-brand-text)', fontSize: 14, lineHeight: 1.35, textAlign: 'right' }}
            >
              10 = <Box component="span">{highLabel}</Box>
            </Typography>
          </Box>
        </Stack>
        <InlineFeedback
          config={config}
          locked={selectionLocked}
          ratingState={ratingState}
          setValue={setValue}
        />
        <RatingSubmitArea
          config={config}
          continueToNext={goNext}
          onEdit={() =>
            setValue(config.id, {
              ...ratingState,
              submitted: false,
            })
          }
          ratingState={ratingState}
          selectedValue={selectedValue}
          submitted={submitted}
          submitValueAndGoNext={submitValueAndGoNext}
        />
      </Stack>
    </Card>
  );
};

export default NpsFormBlock;
